import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';


/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    // ✅ Cascade-delete: when a cart item is removed in the drawer,
    // also remove any addon lines linked to it via _bundleId.
    // We listen in the capture phase so we run before the theme's own handler.
    document.addEventListener('click', this.#handleRemoveClick, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    document.removeEventListener('click', this.#handleRemoveClick, true);
  }

  // ── Cascade-delete handler ──────────────────────────────────────────────────
  /**
   * Intercepts clicks on any cart remove button (drawer OR main cart).
   *
   * If the item being removed is a main product with a _bundleId, we find all
   * addon lines sharing that _bundleId and set their quantity to 0 as well.
   *
   * The main cart page uses handleRemove() in theme.liquid (which already handles
   * child keys via data-child-keys). This handler covers the cart DRAWER where
   * the theme re-renders the section itself after removal.
   *
   * We intentionally do NOT call e.preventDefault() — the theme's own remove
   * logic runs normally; we just fire an extra /cart/update.js alongside it.
   */
  #handleRemoveClick = async (event) => {
    // Match common remove button patterns across Horizon theme variants
    const removeBtn = event.target.closest(
      '[data-cart-remove], .cart-remove-button, [data-line-remove]'
    );
    if (!removeBtn) return;

    // Skip if this is the main cart page — handleRemove() in theme.liquid covers it
    if (removeBtn.hasAttribute('onclick')) return;

    // Get the line key or index stamped on the button by the theme
    const lineKey   = removeBtn.dataset.lineKey   || removeBtn.dataset.key;
    const lineIndex = removeBtn.dataset.lineIndex  || removeBtn.dataset.index;

    if (!lineKey && !lineIndex) return;

    try {
      const cart = await fetch('/cart.js').then((r) => r.json());

      // Find the line being removed
      const removedLine = lineKey
        ? cart.items.find((i) => i.key === lineKey)
        : cart.items[parseInt(lineIndex, 10) - 1]; // line_index is 1-based

      if (!removedLine) return;

      const bundleId  = removedLine.properties?._bundleId;
      const isAddon   = removedLine.properties?._isAddon;

      // Only cascade if this is a main (non-addon) line that has a bundleId
      if (!bundleId || isAddon === 'true') return;

      // Find all addon lines in the same bundle
      const addonLines = cart.items.filter(
        (item) =>
          item.properties?._bundleId === bundleId &&
          item.properties?._isAddon === 'true'
      );

      if (!addonLines.length) return;

      // Set all addon lines to qty 0 (the main line removal is handled by the theme)
      const updates = {};
      addonLines.forEach((addon) => {
        updates[addon.key] = 0;
      });

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });

      console.log('[CartDrawer] Cascade-removed addon lines:', Object.keys(updates));
    } catch (err) {
      console.error('[CartDrawer] Failed to cascade-remove addon:', err);
    }
  };

  // ── Existing methods ────────────────────────────────────────────────────────

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight  = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio         = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}